import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table, Select } from 'antd';
import type { TableColumnsType, ThemeConfig } from 'antd';
import { fontFamily } from '../../theme/tokens';
import Pagination from '../../components/Pagination';
import {
  SearchIcon,
  FilterIcon,
  SortGridIcon,
  EditSquareIcon,
  TrashIcon,
  PlusIcon,
} from '../../assets/icons';

type RoleStatus = 'active' | 'draft' | 'suspended';

interface Role {
  id: string;
  name: string;
  permissions: string[];
  status: RoleStatus;
}

const P_DASH = 'View Dashboard';
const P_USERS = 'View Users';
const P_ROLES = 'Manage Roles';
const P_AUDIT = 'Audit Logs';
const P_APPROVE = 'Approvals';

const SAMPLE_ROLES: Role[] = [
  { id: '1', name: 'State Admin', permissions: [P_DASH, P_USERS], status: 'active' },
  { id: '2', name: 'District President', permissions: [P_DASH, P_USERS], status: 'active' },
  { id: '3', name: 'District Coordinator', permissions: [P_DASH, P_USERS], status: 'active' },
  { id: '4', name: 'IT Auditor', permissions: [P_DASH, P_AUDIT], status: 'draft' },
  { id: '5', name: 'Territory Head', permissions: [P_DASH, P_USERS], status: 'suspended' },
  { id: '6', name: 'Regional Officer', permissions: [P_DASH, P_USERS, P_APPROVE], status: 'active' },
  { id: '7', name: 'Booth Agent', permissions: [P_DASH], status: 'active' },
  { id: '8', name: 'Data Analyst', permissions: [P_DASH, P_AUDIT], status: 'draft' },
  { id: '9', name: 'Compliance Lead', permissions: [P_DASH, P_ROLES, P_AUDIT], status: 'active' },
  { id: '10', name: 'Field Supervisor', permissions: [P_DASH, P_USERS], status: 'suspended' },
  { id: '11', name: 'Content Manager', permissions: [P_DASH, P_APPROVE], status: 'active' },
  { id: '12', name: 'Support Desk', permissions: [P_DASH], status: 'draft' },
];

const STATUS_PILL: Record<RoleStatus, string> = {
  active: 'border-[#bbf7d0] bg-[#dcfce7] text-[#166534]',
  draft: 'border-[#e5e7eb] bg-[#f3f4f6] text-[#374151]',
  suspended: 'border-[#fecaca] bg-[#fee2e2] text-[#b91c1c]',
};
const STATUS_DOT: Record<RoleStatus, string> = {
  active: 'bg-[#22c55e]',
  draft: 'bg-[#9ca3af]',
  suspended: 'bg-[#ef4444]',
};

const TABLE_THEME: ThemeConfig = {
  token: { colorBorderSecondary: '#f3f4f6', fontFamily },
  components: {
    Table: {
      headerBg: '#ffffff',
      headerColor: '#6b7280',
      headerSplitColor: 'transparent',
      borderColor: '#f3f4f6',
      cellPaddingBlock: 24,
      cellPaddingInline: 24,
      rowHoverBg: '#f9fafb',
      colorText: '#111827',
      fontSize: 14,
    },
  },
};

const ACTION_BTN =
  'inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-brand-shade hover:text-brand';

const columns: TableColumnsType<Role> = [
  {
    title: 'Role Name',
    key: 'name',
    width: 240,
    render: (_, r) => <span className="text-sm font-semibold text-[#111827]">{r.name}</span>,
  },
  {
    title: 'Permissions List',
    key: 'permissions',
    width: 320,
    render: (_, r) => (
      <div className="flex flex-wrap gap-2">
        {r.permissions.map((p) => (
          <span
            key={p}
            className="inline-flex items-center rounded border border-[#d1e2ff] bg-brand-shade px-[9px] py-[5px] text-[10px] font-semibold uppercase tracking-[0.25px] text-brand"
          >
            {p}
          </span>
        ))}
      </div>
    ),
  },
  {
    title: 'Status',
    key: 'status',
    width: 190,
    align: 'center',
    render: (_, r) => (
      <span
        className={[
          'inline-flex items-center gap-1.5 rounded-full border px-[11px] py-[5px] text-xs font-medium capitalize',
          STATUS_PILL[r.status],
        ].join(' ')}
      >
        <span className={['size-1.5 rounded-full', STATUS_DOT[r.status]].join(' ')} />
        {r.status}
      </span>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 160,
    align: 'right',
    render: () => (
      <div className="flex items-center justify-end gap-3">
        <button type="button" aria-label="Edit role" className={ACTION_BTN}>
          <EditSquareIcon className="size-4" />
        </button>
        <button
          type="button"
          aria-label="Delete role"
          className="inline-flex size-8 items-center justify-center rounded-lg text-ink-secondary transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="size-[18px]" />
        </button>
      </div>
    ),
  },
];

export default function RolesPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<RoleStatus | ''>('');
  const [sort, setSort] = useState<'reporting' | 'name' | 'status'>('reporting');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = SAMPLE_ROLES.filter((r) => {
      if (q && !r.name.toLowerCase().includes(q)) return false;
      if (status && r.status !== status) return false;
      return true;
    });
    if (sort === 'name') rows.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'status') rows.sort((a, b) => a.status.localeCompare(b.status));
    return rows;
  }, [query, status, sort]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold leading-9 text-ink">Roles and Permissions</h1>
          <p className="text-sm text-ink-secondary">
            Define and manage system-wide operational capacities and access structures.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/roles/new')}
          className="flex items-center justify-center gap-2 self-start rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <PlusIcon className="size-5" />
          Create Role
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-ink-secondary/70" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search roles by name or description..."
            className="w-full rounded-lg border border-[#d1d5db] bg-white py-3 pl-11 pr-3 text-sm text-ink placeholder:text-[#6b7280] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
          />
        </div>

        <div className="lg:w-[200px]">
          <Select
            className="w-full"
            prefix={<FilterIcon className="h-[10px] w-[15px] text-[#374151]" />}
            value={status}
            onChange={(v) => {
              setStatus(v as RoleStatus | '');
              setPage(1);
            }}
            options={[
              { value: '', label: 'Status: All' },
              { value: 'active', label: 'Status: Active' },
              { value: 'draft', label: 'Status: Draft' },
              { value: 'suspended', label: 'Status: Suspended' },
            ]}
          />
        </div>

        <div className="lg:w-[250px]">
          <Select
            className="w-full"
            prefix={<SortGridIcon className="size-5 text-[#6b7280]" />}
            value={sort}
            onChange={(v) => setSort(v as 'reporting' | 'name' | 'status')}
            options={[
              { value: 'reporting', label: 'Sort by: Reporting Person' },
              { value: 'name', label: 'Sort by: Name' },
              { value: 'status', label: 'Sort by: Status' },
            ]}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#e5e7eb] bg-white shadow-card">
        <ConfigProvider theme={TABLE_THEME}>
          <Table<Role>
            className="chunaav-table"
            columns={columns}
            dataSource={pageRows}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>

        {/* Footer */}
        <Pagination
          rangeStart={total === 0 ? 0 : start + 1}
          rangeEnd={Math.min(start + pageSize, total)}
          total={total}
          noun="roles"
          page={current}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
